import { AnnotationType, NORMALIZED_TAG_TO_ANNOTATION_TYPE } from "@perceptron/core/types";
import type { Element, ElementContent, Properties, Root, RootContent } from "hast";
import { isObject, isString } from "lodash-es";

import {
  isValidBoxContent,
  isValidPointContent,
  isValidPolygonContent,
  isValidTimestampString,
} from "@demo/utils/coordinateParsing";

// Counter is shared across every annotation-producing tag, in document order.
// MUST stay in lock-step with `parseAnnotations` in store/model.ts. Waypoint
// tags inside a `<track>` consume zero ids (they're internal data) — the
// walker tracks an `insideTrack` flag to skip them.
//
// `<track>` is aliased to `<vlm-track>` upstream (remarkTrackAlias) to
// sidestep HTML's void-element treatment. Tag list lives in
// `NORMALIZED_TAG_TO_ANNOTATION_TYPE` (single source of truth).

const WAYPOINT_TAG_TO_KIND: Record<string, "point_box" | "point" | "polygon"> = {
  "point-box": "point_box",
  point: "point",
  polygon: "polygon",
};

function getNodeText(node: Element): string {
  let text = "";
  for (const child of node.children || []) {
    if (child.type === "text") {
      text += child.value;
    }
  }
  return text;
}

function readStringAttr(props: Properties | undefined, name: string): string | undefined {
  const v = props?.[name];
  return isString(v) ? v : undefined;
}

/**
 * Both validity rules MUST mirror the parser in store/model.ts so a tag the
 * parser would drop (and therefore not register in the annotation store) also
 * gets skipped here — otherwise our id counter races ahead and every later
 * chip looks up the wrong store entry.
 */
function isValidAnnotationContent(
  annotationType: AnnotationType,
  nodeText: string,
  props: Properties | undefined,
): boolean {
  switch (annotationType) {
    case AnnotationType.POINT:
      return isValidPointContent(nodeText);
    case AnnotationType.BOX: {
      if (!isValidBoxContent(nodeText)) return false;
      // Optional `t` — present-but-malformed makes the parser throw and skip,
      // so we must skip too.
      const t = readStringAttr(props, "t");
      return t === undefined || isValidTimestampString(t);
    }
    case AnnotationType.POLYGON:
      return isValidPolygonContent(nodeText);
    case AnnotationType.CLIP: {
      // `t` is required; the parser drops a clip without one.
      const t = readStringAttr(props, "t");
      return t !== undefined && isValidTimestampString(t);
    }
    case AnnotationType.TRACK:
    case AnnotationType.COLLECTION:
      // Containers — only their descendants carry coordinate content
      return true;
    default:
      return true;
  }
}

interface WalkState {
  counter: number;
  /** Per-type counters for the default-label fallback (so "Collection 3"
   *  refers to the 3rd collection, not the 7th overall annotation). */
  typeCounters: Record<AnnotationType, number>;
}

function isTaggedElement(node: ElementContent | RootContent | Element): node is Element {
  return node.type === "element" && "tagName" in node && isString(node.tagName);
}

/**
 * Numbers each annotation tag in document order. Inside a `<track>`,
 * descendants are flagged `data-waypoint="true"` and skipped by the counter.
 */
function walkAnnotations(node: Element, state: WalkState, insideTrack: boolean): void {
  const tagName = node.tagName;
  const annotationType = (NORMALIZED_TAG_TO_ANNOTATION_TYPE as Record<string, AnnotationType>)[tagName];
  const props = isObject(node.properties) ? node.properties : undefined;

  if (tagName === "vlm-track") {
    if (props) {
      state.counter++;
      state.typeCounters[AnnotationType.TRACK]++;
      props["number"] = state.counter;
      props["data-label-number"] = state.typeCounters[AnnotationType.TRACK];
      props["data-tag-name"] = tagName;
      props["data-annotation-type"] = AnnotationType.TRACK;
      const firstWaypoint = (node.children ?? []).find(
        (c): c is Element => isTaggedElement(c) && c.tagName in WAYPOINT_TAG_TO_KIND,
      );
      if (firstWaypoint) props["data-waypoint-kind"] = WAYPOINT_TAG_TO_KIND[firstWaypoint.tagName];
    }
    for (const child of node.children ?? []) {
      if (isTaggedElement(child)) walkAnnotations(child, state, true);
    }
    return;
  }

  if (annotationType !== undefined && props) {
    if (insideTrack) {
      // Waypoint — no number, so its React component returns null.
      props["data-tag-name"] = tagName;
      props["data-waypoint"] = "true";
    } else {
      const nodeText = getNodeText(node);
      props["data-tag-name"] = tagName;
      if (isValidAnnotationContent(annotationType, nodeText, props)) {
        state.counter++;
        state.typeCounters[annotationType]++;
        props["number"] = state.counter;
        props["data-label-number"] = state.typeCounters[annotationType];
        props["data-annotation-type"] = annotationType;
      }
    }
  }

  for (const child of node.children ?? []) {
    if (isTaggedElement(child)) walkAnnotations(child, state, insideTrack);
  }
}

/** Where numbering stands after a stretch of the document; see `AnnotationChain`. */
export type AnnotationCount = WalkState;

/**
 * Numbering carried across a document rendered in blocks (`MarkdownBlocks`): block `index` starts from the
 * count block `index - 1` ended on and records its own end, so every block numbers its annotations exactly
 * as a single render of the whole document would. Blocks render in order, so the previous end is current.
 */
export interface AnnotationChain {
  ends: AnnotationCount[];
  index: number;
}

const freshCount = (): AnnotationCount => ({
  counter: 0,
  typeCounters: {
    [AnnotationType.POINT]: 0,
    [AnnotationType.BOX]: 0,
    [AnnotationType.POLYGON]: 0,
    [AnnotationType.CLIP]: 0,
    [AnnotationType.COLLECTION]: 0,
    [AnnotationType.TRACK]: 0,
  },
});

export const rehypeAnnotationProcessor = ({ chain }: { chain?: AnnotationChain } = {}) => {
  return (tree: Root) => {
    const previous = chain && chain.index > 0 ? chain.ends[chain.index - 1] : undefined;
    const state: WalkState = previous
      ? { counter: previous.counter, typeCounters: { ...previous.typeCounters } }
      : freshCount();
    for (const child of tree.children ?? []) {
      if (isTaggedElement(child)) walkAnnotations(child, state, false);
    }
    if (chain) chain.ends[chain.index] = state;
  };
};
